import { inject, Injectable, signal } from '@angular/core';
import { map, Observable, shareReplay, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { County } from '../core/classes/county';
import { Territory } from '../core/classes/territory';
import { GenEiaRegion } from '../generated/serverModels/GenEiaRegion';
import * as LocationActions from '../state/location/location.actions';
import { GenStateAccessRequest } from '../generated/serverModels/GenStateAccessRequest';
import { GenStateAccess } from '../generated/serverModels/GenStateAccess';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private http = inject(HttpClient);

  private store = inject(Store);
  private cache = new Map<string, Observable<unknown>>();

  public loadingCounties = signal(false);
  public excludedTerritories: string[] = ['VI', 'AS', 'MP', 'GU', 'PR'];

  constructor() { }

  public getAllTerritories(): Observable<Territory[]> {
    const url = 'api/location/all-states';
    let cached = this.cache.get(url);
    if (!cached) {
      cached = this.http.get<Territory[]>(url).pipe(
        map((territories) =>
          territories
            .map((territory) => new Territory(territory))
            .filter((s) => !this.excludedTerritories.includes(s.abbreviation))
            .sort((a, b) => {
              if (a.name.toUpperCase() > b.name.toUpperCase()) return 1;
              if (a.name.toUpperCase() < b.name.toUpperCase()) return -1;
              return 0;
            }),
        ),
        tap((territories) =>
          this.store.dispatch(
            LocationActions.LoadTerritoryDataSuccess({ territories }),
          ),
        ),
        shareReplay(1),
      );
      this.cache.set(url, cached);
    }
    return cached as Observable<Territory[]>;
  }

  public getApprovedStates(userId: number): Observable<Territory[]> {
    return this.http
      .get<Territory[]>('api/location/default-states', { params: { userId } })
      .pipe(map((states) => states.map((s) => new Territory(s))));
  }

  public setApprovedStates(
    userId: number,
    abbreviations: string[],
  ): Observable<void> {
    return this.http.post<void>(
      `api/location/default-states/${userId}`,
      abbreviations,
    );
  }

  public hasNationalAccess(userId: number): Observable<boolean> {
    return this.http.get<boolean>(
      `api/location/default-states/${userId}/national-access`,
    );
  }

  public grantNationalAccess(userId: number): Observable<void> {
    return this.http.put<void>(
      `api/location/default-states/${userId}/national-access`,
      {},
    );
  }

  public submitStateAccessRequest(payload: GenStateAccess) {
    const url = 'api/location/state-access-requests';
    return this.http.post(url, payload);
  }

  public getStateAccessRequests(userId: number): Observable<GenStateAccessRequest[]> {
    const url = 'api/location/state-access-requests';
    return this.http.get<GenStateAccessRequest[]>(url, { params: { userId } }).pipe(
      map((requests) =>
        requests.map((request) => new GenStateAccessRequest(request)),
      ),
    );
  }
  public approveStateAccessRequest(requestId: number): Observable<void> {
    const url = `api/location/state-access-requests/approve/${requestId}`;
    return this.http.put<void>(url, {});
  }

  public denyStateAccessRequest(requestId: number, denialReason: string): Observable<void> {
    const url = `api/location/state-access-requests/deny/${requestId}`;
    return this.http.put<void>(url, { denialReason });
  }

  public getAllCounties(): Observable<County[]> {
    const url = 'api/location/all-counties';
    let cached = this.cache.get(url);
    if (!cached) {
      this.loadingCounties.set(true);
      cached = this.http.get<County[]>(url).pipe(
        map((counties) => counties.map((county) => new County(county))),
        tap((counties) => {
          this.loadingCounties.set(false);
          this.store.dispatch(
            LocationActions.LoadCountyDataSuccess({ counties }),
          );
        }),
        shareReplay(1),
      );
      this.cache.set(url, cached);
    }
    return cached as Observable<County[]>;
  }

  public loadRegions() {
    const url = 'api/location/eia-regions';
    let cached = this.cache.get(url);
    if (!cached) {
      cached = this.http.get<GenEiaRegion[]>(url).pipe(
        map(regions => regions.map(region => new GenEiaRegion(region))),
        shareReplay(1),
      );
      this.cache.set(url, cached);
    }
    return cached as Observable<GenEiaRegion[]>;
  }
}
